import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import {
    dismissAllNotifications,
    dismissNotification,
    fetchNotificationInbox,
    markAllNotificationsRead,
    markNotificationRead,
} from '../lib/api'

const Ctx = createContext(undefined)

function localId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function isServerId(value) {
    return value !== null && value !== undefined && /^\d+$/.test(String(value))
}

function normalizeNotification(raw) {
    const basePayload = raw?.payload && typeof raw.payload === 'object' ? raw.payload : (raw && typeof raw === 'object' ? raw : {})
    const payload = { ...basePayload }
    if (raw?.title && !payload.title) payload.title = raw.title
    if (raw?.message && !payload.summary && !payload.message) payload.summary = raw.message
    if (raw?.linkUrl && !payload.link) payload.link = raw.linkUrl
    const serverId = raw?.id ?? payload.notificationId ?? null
    const id = serverId ?? localId()
    return {
        id,
        serverId: isServerId(serverId) ? serverId : null,
        dedupeKey: raw?.dedupeKey || payload.notificationDedupeKey || null,
        payload,
        readAt: raw?.readAt || payload.readAt || null,
        receivedAt: raw?.createdAt || payload.receivedAt || new Date().toISOString(),
        receivedAtDisplay: raw?.createdAtDisplay || payload.receivedAtDisplay || null,
    }
}

function mergeNotification(existing, next) {
    const nextKey = next.serverId ? `id:${next.serverId}` : (next.dedupeKey ? `dedupe:${next.dedupeKey}` : null)
    if (!nextKey) return [next, ...existing].slice(0, 100)
    const filtered = existing.filter((item) => {
        const key = item.serverId ? `id:${item.serverId}` : (item.dedupeKey ? `dedupe:${item.dedupeKey}` : null)
        return key !== nextKey
    })
    return [next, ...filtered].slice(0, 100)
}

export function NotificationProvider({ children }) {
    const { user, isAuthenticated } = useAuth()
    const userKey = user?.email || user?.id || ''
    const [badgeCount, setBadgeCount] = useState(0)
    const [notifications, setNotifications] = useState([])

    useEffect(() => {
        let cancelled = false
        async function load() {
            if (!isAuthenticated || !userKey) {
                setBadgeCount(0)
                setNotifications([])
                return
            }
            try {
                const data = await fetchNotificationInbox(50)
                if (cancelled) return
                const items = Array.isArray(data?.items) ? data.items.map(normalizeNotification) : []
                setNotifications(items)
                const unread = Number(data?.unreadCount)
                setBadgeCount(Number.isFinite(unread) ? unread : items.filter((item) => !item.readAt).length)
            } catch {
                if (!cancelled) {
                    setBadgeCount(0)
                    setNotifications([])
                }
            }
        }
        void load()
        return () => { cancelled = true }
    }, [isAuthenticated, userKey])

    useEffect(() => {
        function onNew(e) {
            const item = normalizeNotification({ payload: e?.detail || {}, id: e?.detail?.notificationId, createdAt: e?.detail?.receivedAt })
            setBadgeCount((n) => n + 1)
            setNotifications((arr) => mergeNotification(arr, item))
        }
        function onDismiss(e) {
            const signupId = e?.detail?.signupId
            if (signupId === null || signupId === undefined || signupId === '') return
            setNotifications((arr) => {
                const removedUnread = arr.filter((item) => (
                    item?.payload?.type === 'SIGNUP_REQUEST_CREATED'
                    && String(item?.payload?.signupId) === String(signupId)
                    && !item.readAt
                )).length
                if (removedUnread > 0) {
                    setBadgeCount((count) => Math.max(0, count - removedUnread))
                }
                return arr.filter((item) => !(
                    item?.payload?.type === 'SIGNUP_REQUEST_CREATED'
                    && String(item?.payload?.signupId) === String(signupId)
                ))
            })
        }
        window.addEventListener('notifications:new', onNew)
        window.addEventListener('notifications:dismiss', onDismiss)
        return () => {
            window.removeEventListener('notifications:new', onNew)
            window.removeEventListener('notifications:dismiss', onDismiss)
        }
    }, [])

    const value = useMemo(() => ({
        badgeCount,
        notifications,
        markAllRead: () => {
            setBadgeCount(0)
            const now = new Date().toISOString()
            setNotifications((arr) => arr.map((item) => ({ ...item, readAt: item.readAt || now })))
            void markAllNotificationsRead().catch(() => {})
        },
        clear: () => {
            setBadgeCount(0)
            setNotifications([])
            void dismissAllNotifications().catch(() => {})
        },
        dismiss: (id) => {
            let serverId = null
            setNotifications((arr) => {
                const target = arr.find((item) => String(item.id) === String(id))
                serverId = target?.serverId || null
                if (target && !target.readAt) {
                    setBadgeCount((n) => (n > 0 ? n - 1 : 0))
                }
                return arr.filter((item) => String(item.id) !== String(id))
            })
            if (serverId) void dismissNotification(serverId).catch(() => {})
        },
        markRead: (id) => {
            let serverId = null
            setNotifications((arr) => arr.map((item) => {
                if (String(item.id) !== String(id)) return item
                serverId = item.serverId || null
                if (!item.readAt) setBadgeCount((n) => (n > 0 ? n - 1 : 0))
                return { ...item, readAt: item.readAt || new Date().toISOString() }
            }))
            if (serverId) void markNotificationRead(serverId).catch(() => {})
        },
    }), [badgeCount, notifications])

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useNotifications() {
    const ctx = useContext(Ctx)
    if (!ctx) throw new Error('useNotifications must be used within NotificationProvider')
    return ctx
}
